/**
 * Binomial (Wald) intervals for per-answer rates: half-width
 * = z * sqrt(p(1-p) / n). The same math as the web app's
 * `apps/web/modules/saas/shared/lib/data-accuracy.ts` (`ciHalfWidth`), moved
 * here so the persona lens can compute intervals server-side and any client
 * can import it from `@repo/core/client`.
 *
 * Pure: safe for `@repo/core/client`.
 */

const Z_SCORES: Record<number, number> = {
	0.8: 1.282,
	0.9: 1.645,
	0.95: 1.96,
};

/** z-score for a two-sided confidence level (0.8, 0.9, 0.95; default 0.9). */
export function zScoreFor(confidence: number): number {
	return Z_SCORES[confidence] ?? 1.645;
}

/**
 * Half-width of the interval around proportion `p` (0 to 1) observed over
 * `n` answers, as a proportion, capped at 1. 1 when n < 1.
 */
export function ciHalfWidth(n: number, p: number, confidence = 0.9): number {
	if (n < 1) {
		return 1;
	}
	const q = Math.min(1, Math.max(0, p));
	return Math.min(1, zScoreFor(confidence) * Math.sqrt((q * (1 - q)) / n));
}

/**
 * Half-width in percentage points for a rate given in percent (0 to 100).
 * A null rate (n = 0) is treated as 0; the result is 100 when n < 1.
 */
export function rateCiHalfWidthPct(
	n: number,
	ratePct: number | null,
	confidence = 0.9,
): number {
	return ciHalfWidth(n, (ratePct ?? 0) / 100, confidence) * 100;
}

/**
 * Half-width in percentage points of the DIFFERENCE between two independent
 * rates (percent): z * sqrt(p1(1-p1)/n1 + p2(1-p2)/n2). 100 when either side
 * has no answers.
 */
export function rateDiffCiHalfWidthPct(
	a: { n: number; ratePct: number | null },
	b: { n: number; ratePct: number | null },
	confidence = 0.9,
): number {
	if (a.n < 1 || b.n < 1) {
		return 100;
	}
	const p1 = Math.min(1, Math.max(0, (a.ratePct ?? 0) / 100));
	const p2 = Math.min(1, Math.max(0, (b.ratePct ?? 0) / 100));
	return Math.min(
		100,
		zScoreFor(confidence) *
			Math.sqrt((p1 * (1 - p1)) / a.n + (p2 * (1 - p2)) / b.n) *
			100,
	);
}
